import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Star, TrendingUp, ShoppingBag, Eye } from 'lucide-react';

const Categories = ({ onCategorySelect }) => {
  const [hoveredCategory, setHoveredCategory] = useState(null);

  const categories = [
    {
      id: 1,
      name: 'Electronics',
      description: 'Latest gadgets and tech',
      image: 'https://images.unsplash.com/photo-1498049794561-7780e7231661?w=400&h=300&fit=crop',
      products: 1250,
      trending: true,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      id: 2,
      name: 'Fashion',
      description: 'Trendy clothing and accessories',
      image: 'https://images.unsplash.com/photo-1445205170230-053b83016050?w=400&h=300&fit=crop',
      products: 890,
      trending: false,
      color: 'from-pink-500 to-rose-500'
    },
    {
      id: 3,
      name: 'Home & Garden',
      description: 'Everything for your home',
      image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&h=300&fit=crop',
      products: 654,
      trending: false,
      color: 'from-green-500 to-emerald-500'
    },
    {
      id: 4,
      name: 'Sports & Fitness',
      description: 'Stay active and healthy',
      image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop',
      products: 432,
      trending: true,
      color: 'from-orange-500 to-red-500'
    },
    {
      id: 5,
      name: 'Books & Media',
      description: 'Knowledge and entertainment',
      image: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=400&h=300&fit=crop',
      products: 765,
      trending: false,
      color: 'from-purple-500 to-indigo-500'
    },
    {
      id: 6,
      name: 'Beauty & Health',
      description: 'Look and feel your best',
      image: 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=400&h=300&fit=crop',
      products: 543,
      trending: true,
      color: 'from-teal-500 to-cyan-500'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const handleCategoryClick = (category) => {
    // استدعاء دالة تمرير الفئة
    if (onCategorySelect) {
      onCategorySelect(category.name);
    }
    
    // التمرير إلى قسم المنتجات
    const productsSection = document.getElementById('products');
    if (productsSection) {
      productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header Section */}
      <motion.div
        className="text-center mb-16"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
<motion.div
  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full mb-6 mt-6"
  initial={{ scale: 0 }}
  whileInView={{ scale: 1 }}
  viewport={{ once: true }}
  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
>
  <Star className="w-5 h-5 text-blue-600 mr-2" />
  <span className="text-blue-800 font-semibold">Shop by Category</span>
</motion.div>

        <motion.h2
          className="text-4xl md:text-5xl font-black text-gray-900 mb-6 leading-tight"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          Discover Amazing
          <span className="block bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent leading-tight">
            Categories
          </span>
        </motion.h2>

        <motion.p
          className="text-xl text-gray-600 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
        >
          Browse through our carefully curated categories and find exactly what you're looking for
        </motion.p>
      </motion.div>

      {/* Categories Grid */}
      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        {categories.map((category) => (
          <motion.div
            key={category.id}
            className="group relative cursor-pointer"
            variants={cardVariants}
            onMouseEnter={() => setHoveredCategory(category.id)}
            onMouseLeave={() => setHoveredCategory(null)}
            onClick={() => handleCategoryClick(category)}
            whileHover={{ y: -10 }}
            whileTap={{ scale: 0.98 }}
          >
            {/* Card Container */}
            <div className="relative bg-white rounded-3xl shadow-lg overflow-hidden border border-gray-100 group-hover:shadow-2xl transition-all duration-500">
              {/* Image Section */}
              <div className="relative h-64 overflow-hidden">
                <motion.img
                  src={category.image}
                  alt={category.name}
                  className="w-full h-full object-cover"
                  initial={{ scale: 1 }}
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.6 }}
                />
                
                {/* Gradient Overlay */}
                <div className={`absolute inset-0 bg-gradient-to-t ${category.color} opacity-20 group-hover:opacity-30 transition-opacity duration-300`} />
                
                {/* Trending Badge */}
                {category.trending && (
                  <motion.div
                    className="absolute top-4 right-4 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1 rounded-full flex items-center space-x-1 shadow-lg"
                    initial={{ scale: 0, rotate: -10 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
                  >
                    <TrendingUp size={14} />
                    <span className="text-sm font-bold">Trending</span>
                  </motion.div>
                )}

                {/* Hover Actions */}
                <motion.div
                  className="absolute inset-0 flex items-center justify-center space-x-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: hoveredCategory === category.id ? 1 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.button
                    className="bg-white/90 backdrop-blur-sm text-gray-900 p-3 rounded-full shadow-lg hover:bg-white transition-all duration-300"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCategoryClick(category);
                    }}
                  >
                    <Eye size={20} />
                  </motion.button>
                  <motion.button
                    className="bg-white/90 backdrop-blur-sm text-gray-900 p-3 rounded-full shadow-lg hover:bg-white transition-all duration-300"
                    whileHover={{ scale: 1.1, rotate: -5 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <ShoppingBag size={20} />
                  </motion.button>
                </motion.div>

                {/* Animated Border */}
                <motion.div
                  className="absolute inset-0 border-4 border-transparent"
                  animate={{
                    borderColor: hoveredCategory === category.id 
                      ? ['rgba(59, 130, 246, 0)', 'rgba(59, 130, 246, 0.5)', 'rgba(59, 130, 246, 0)']
                      : 'rgba(59, 130, 246, 0)'
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              </div>

              {/* Content Section */}
              <div className="p-6">
                <motion.div
                  className="flex items-center justify-between mb-3"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <h3 className="text-2xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
                    {category.name}
                  </h3>
                  <motion.div
                    className="text-gray-400 group-hover:text-blue-600 transition-colors duration-300"
                    whileHover={{ x: 5 }}
                  >
                    <ArrowRight size={24} />
                  </motion.div>
                </motion.div>

                <motion.p
                  className="text-gray-600 mb-4"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  {category.description}
                </motion.p>

                <motion.div
                  className="flex items-center justify-between"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${category.color}`} />
                    <span className="text-sm text-gray-500 font-medium">
                      {category.products.toLocaleString()} products
                    </span>
                  </div>
                  
                  <motion.button
                    className="text-sm font-bold text-blue-600 hover:text-blue-700 transition-colors duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCategoryClick(category);
                    }}
                  >
                    Explore →
                  </motion.button>
                </motion.div>
              </div>

              {/* Animated Background Pattern */}
              <motion.div
                className="absolute inset-0 opacity-5 pointer-events-none"
                style={{
                  backgroundImage: `radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.3) 0%, transparent 50%),
                                   radial-gradient(circle at 80% 20%, rgba(147, 51, 234, 0.3) 0%, transparent 50%)`
                }}
                animate={{
                  backgroundPosition: hoveredCategory === category.id 
                    ? ['0% 0%', '100% 100%', '0% 0%'] 
                    : '0% 0%'
                }}
                transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
              />
            </div>

            {/* Glow Effect */}
            <motion.div
              className={`absolute inset-0 -z-10 blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500 bg-gradient-to-r ${category.color}`}
              animate={{
                scale: hoveredCategory === category.id ? 1.05 : 1,
              }}
              transition={{ duration: 0.3 }}
            />
          </motion.div>
        ))}
      </motion.div>

      {/* Call to Action */}
      <motion.div
        className="text-center mt-16"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.8 }}
      >
        <motion.button
          className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 text-white px-8 py-4 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
          whileHover={{ scale: 1.05, y: -2 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => onCategorySelect && onCategorySelect(null)}
        >
          View All Categories
        </motion.button>
      </motion.div>
    </div>
  );
};

export default Categories;
